'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import SiteFooter from '../components/SiteFooter'
import BlogCtaBanner from '../components/BlogCtaBanner'
import { fetchBlogPostBySlug, fetchRelatedPosts } from '../lib/blogApi'
import { splitContentForCta } from '../lib/blogContentSplit'
import contentSnapshot from '../data/contentSnapshot.json'
import { primaryClusterForPost } from '../../content/clusters'
import { teamMemberByAuthorName } from '../../content/team'
import { ArrowRight } from 'lucide-react'

const SEEDED_POSTS = contentSnapshot.blogPosts || []

function findSeededPost(slug) {
  return SEEDED_POSTS.find((post) => post.slug === slug) || null
}

function formatBlogDate(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function BlogPostPage() {
  const { slug } = useParams()
  const seededPost = findSeededPost(slug)
  const [post, setPost] = useState(seededPost)
  const [status, setStatus] = useState(seededPost ? 'found' : 'loading') // 'loading' | 'found' | 'notfound'
  const [relatedPosts, setRelatedPosts] = useState([])

  // Re-seed synchronously during render when the slug changes (SPA navigation)
  // so real snapshot content is present on the first frame. This is React's
  // documented "adjust state while rendering" pattern it never wipes a
  // snapshot match to a loading/empty state.
  const [renderedSlug, setRenderedSlug] = useState(slug)
  if (renderedSlug !== slug) {
    setRenderedSlug(slug)
    setPost(seededPost)
    setStatus(seededPost ? 'found' : 'loading')
    setRelatedPosts([])
  }

  useEffect(() => {
    let cancelled = false
    const seeded = findSeededPost(slug)

    fetchBlogPostBySlug(slug)
      .then((result) => {
        if (cancelled) return
        if (result) {
          setPost(result)
          setStatus('found')
          fetchRelatedPosts(result.category, result.slug, 3)
            .then((related) => {
              if (!cancelled) setRelatedPosts(Array.isArray(related) ? related : [])
            })
            .catch(() => {
              if (!cancelled) setRelatedPosts([])
            })
        } else if (!seeded) {
          setStatus('notfound')
        }
      })
      .catch(() => {
        if (!cancelled && !seeded) setStatus('notfound')
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  // While navigating between slugs the effect re-fetches; treat a post that
  // belongs to a previous slug as still-loading so we never flash stale content.
  const isStalePost = post && post.slug && slug && post.slug !== slug

  if (status === 'loading' || isStalePost) {
    return (
      <main className="section section-white blog-post-page">
        {/* This transient loading title/robots was set via Helmet; it never
            reaches a crawler (build-time generateMetadata in
            app/blog/[slug]/page.tsx renders the real post metadata directly
            for known slugs, or the not-found metadata otherwise) so it is not
            ported. */}
        <div className="container client-study-state" role="status" aria-live="polite">
          <span className="client-study-state-spinner" aria-hidden="true" />
          <p className="client-study-state-text">Loading post…</p>
        </div>
      </main>
    )
  }

  if (status === 'notfound' || !post) {
    return (
      <main className="section section-white blog-post-page">
        {/* Not-found title/description/robots ported to app/blog/[slug]/page.tsx's
            generateMetadata fallback branch. */}
        <div className="container client-study-notfound">
          <span className="eyebrow-label">404 Blog</span>
          <h1>Post not found</h1>
          <p className="client-study-notfound-intro">
            The blog post you're looking for doesn't exist, may have been moved, or isn't
            published yet. Explore our other GoHighLevel guides and case studies instead.
          </p>
          <div className="client-study-notfound-actions">
            <Link href="/blog" className="primary-pill large">Browse all posts</Link>
            <Link href="/booking" className="secondary-pill">Book a free consultation</Link>
          </div>
        </div>
        <SiteFooter />
      </main>
    )
  }

  const canonical = `https://ghlprime.com/blog/${slug}`
  const metaTitle = post.seo_title || post.title
  const metaDescription = post.seo_description || post.excerpt
  // Never emit an empty og:image. Use the post's own cover when it has one,
  // otherwise fall back to the blog hub card.
  const shareImage = post.cover_image || 'https://ghlprime.com/og-ghlprime-gohighlevel-expert-agency.png'
  // T-11.4: every post links back to its most relevant service page, both in
  // the intro and the conclusion, via one shared cluster map (content/clusters.ts)
  // rather than a hand-edited link in each of 113 posts.
  const relatedService = primaryClusterForPost(post)
  const authorMember = teamMemberByAuthorName(post.author)

  return (
    <main className="section section-white blog-post-page">
      {/* title/description/keywords/canonical/og:* and twitter:* now come from
          app/blog/[slug]/page.tsx's generateMetadata (same values, computed
          the same way). JSON-LD stays here, unchanged. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          '@id': canonical + '#article',
          headline: post.title,
          description: post.excerpt,
          image: shareImage,
          // A generic byline ("GHL Prime Team") is the organisation itself. A
          // named author who matches a known team member (content/team.ts)
          // references that SAME Person node the root layout's @graph
          // already defines at #<slug> (T-12.3) -- never a second,
          // un-identified Person for someone who already has a page. A named
          // author with no matching team-member page falls back to an
          // inline Person with just a name (still correct, just unlinked).
          author: (() => {
            if (!post.author || /^ghl prime( team)?$/i.test(post.author.trim())) {
              return { '@id': 'https://ghlprime.com/#org' }
            }
            const member = teamMemberByAuthorName(post.author)
            return member ? { '@id': `https://ghlprime.com/#${member.slug}` } : { '@type': 'Person', name: post.author }
          })(),
          publisher: { '@id': 'https://ghlprime.com/#org' },
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
          articleSection: post.category,
          inLanguage: 'en-US',
          isPartOf: { '@id': 'https://ghlprime.com/#website' },
        }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ghlprime.com/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://ghlprime.com/blog' },
            { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
          ],
        }) }} />

      <article className="container blog-article">
        <nav className="blog-breadcrumb" aria-label="Breadcrumb">
          <Link href="/">Home</Link>
          <span aria-hidden="true">/</span>
          <Link href="/blog">Blog</Link>
          <span aria-hidden="true">/</span>
          <span className="blog-breadcrumb-current">{post.title}</span>
        </nav>

   

        <h1 className="blog-article-title">{post.title}</h1>

        <div className="blog-meta-bar">
          {authorMember ? (
            <Link href={`/team/${authorMember.slug}`}>{authorMember.name}</Link>
          ) : (
            <span>{post.author || 'GHL Prime Team'}</span>
          )}
          <span aria-hidden="true">|</span>
          <span>{formatBlogDate(post.published_at)}</span>
          {post.updated_at && post.updated_at.slice(0, 10) !== (post.published_at || '').slice(0, 10) ? (
            <>
              <span aria-hidden="true">|</span>
              {/* Same field BlogPosting's dateModified uses (below) -- this
                  is the one visible place that date has to agree with. */}
              <span>Last updated {formatBlogDate(post.updated_at)}</span>
            </>
          ) : null}
          {post.reading_time ? (
            <>
              <span aria-hidden="true">|</span>
              <span>{post.reading_time} min read</span>
            </>
          ) : null}
          <span className="blog-card-badge">{post.category}</span>
        </div>

        {relatedService ? (
          <p className="blog-related-service-intro">
            Need help with this? See our{' '}
            <Link href={relatedService.serviceUrl}>{relatedService.serviceName} service</Link>.
          </p>
        ) : null}

        {/* A CTA banner and a sources fold only ever appear for a post that
            actually carries cta_variant/sources -- both are new, optional
            Blog Writer columns every pre-existing post has as null, so this
            renders exactly as it always did for every post written before
            this feature existed. */}
        {post.cta_variant ? (
          (() => {
            const { before, after } = splitContentForCta(post.content)
            return (
              <>
                <div className="blog-content" dangerouslySetInnerHTML={{ __html: before }} />
                <BlogCtaBanner variant={post.cta_variant} />
                {after ? <div className="blog-content" dangerouslySetInnerHTML={{ __html: after }} /> : null}
              </>
            )
          })()
        ) : (
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        )}

        {Array.isArray(post.sources) && post.sources.length ? (
          <section className="blog-sources-fold" aria-label="Sources">
            <details>
              <summary>Sources ({post.sources.length})</summary>
              <ul>
                {post.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">{source.name || source.url}</a>
                  </li>
                ))}
              </ul>
            </details>
          </section>
        ) : null}

        {relatedService ? (
          <p className="blog-related-service-outro">
            Want this done for you? GHL Prime's{' '}
            <Link href={relatedService.serviceUrl}>{relatedService.serviceName} service</Link>{' '}
            handles this end to end.
          </p>
        ) : null}

        {relatedPosts.length ? (
          <section className="blog-related" aria-labelledby="blog-related-heading">
            <h2 id="blog-related-heading">Related posts</h2>
            <div className="blog-grid">
              {relatedPosts.map((related) => (
                <article key={related.slug} className="blog-card">
                  <Link href={`/blog/${related.slug}`} className="blog-card-link" aria-label={`Read ${related.title}`}>
                    {related.cover_image ? (
                      <div className="blog-card-image-wrap">
                        <img src={related.cover_image} alt={related.title} className="blog-card-image" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }} />
                      </div>
                    ) : null}
                    <div className="blog-card-body">
                      <span className="blog-card-badge">{related.category}</span>
                      <h3>{related.title}</h3>
                      <p className="blog-card-excerpt">{related.excerpt}</p>
                      <div className="blog-card-meta">
                        <span>{formatBlogDate(related.published_at)}</span>
                        {related.reading_time ? (
                          <>
                            <span aria-hidden="true">•</span>
                            <span>{related.reading_time} min read</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="blog-cta-box">
          <h2>Need help implementing this in GoHighLevel?</h2>
          <p>Our team builds, automates, and scales GoHighLevel systems for agencies every day. Book a free call and we'll map out exactly what to ship next.</p>
          <Link href="/booking" className="primary-pill large">Book a free call</Link>
        </section>
      </article>

      <SiteFooter />
    </main>
  )
}
